import { AUTH_TYPES } from './authTypes';

/**
 * Maps URL referrals object from frontend format to backend field names
 * @param {Object} urlReferrals - URL referrals object with keys: tg, obesid, drtc
 * @returns {Object} Mapped object with backend field names
 */
export const mapUrlReferralsToBackend = urlReferrals => ({
  urlReferralTG: urlReferrals.tg || null,
  urlReferralObesiD: urlReferrals.obesid || null,
  urlReferralDRTC: urlReferrals.drtc || null,
});

/**
 * Maps URL referrals from backend format to frontend format
 * @param {Object} data - Backend data with urlReferralTG, urlReferralObesiD, urlReferralDRTC
 * @returns {Object} URL referrals object with keys: tg, obesid, drtc
 */
export const mapUrlReferralsToFrontend = data => ({
  tg: data.urlReferralTG || '',
  obesid: data.urlReferralObesiD || '',
  drtc: data.urlReferralDRTC || '',
});

/**
 * Builds the submission payload for institution enquiry
 * @param {Object} formData - Form state data
 * @param {boolean} isExisting - Whether this is an existing institution
 * @param {boolean} shouldShowFTE - Whether to include FTE number
 * @param {boolean} shouldShowDetailsSection - Whether to include institution details
 * @returns {Object} Formatted payload for API submission
 */
export const buildSubmissionPayload = (
  formData,
  isExisting,
  shouldShowFTE,
  shouldShowDetailsSection
) => {
  // Build institution details object
  const institutionDetails = {
    // Include the appropriate boolean based on institution type
    ...(isExisting
      ? { isSameUserBase: formData.isSameUserBase }
      : { isAccountAdmin: formData.isAccountAdmin }),
    // Include FTE number if applicable
    fteNumber: shouldShowFTE ? formData.fteNumber : null,
    // Include authentication details if section is shown
    authenticationType: shouldShowDetailsSection ? formData.authenticationType : [],
  };

  // Add IP address if IP authentication is selected
  if (shouldShowDetailsSection && formData.authenticationType.includes(AUTH_TYPES.IP)) {
    institutionDetails.ipAddress = formData.ipAddress;
  } else {
    institutionDetails.ipAddress = null;
  }

  // Add URL referrals if URL authentication is selected
  if (shouldShowDetailsSection && formData.authenticationType.includes(AUTH_TYPES.URL)) {
    const mappedUrls = mapUrlReferralsToBackend(formData.urlReferrals);
    Object.assign(institutionDetails, mappedUrls);
  } else {
    institutionDetails.urlReferralTG = null;
    institutionDetails.urlReferralObesiD = null;
    institutionDetails.urlReferralDRTC = null;
  }

  // Add OpenAthens ID if OpenAthens authentication is selected
  if (shouldShowDetailsSection && formData.authenticationType.includes(AUTH_TYPES.OPENATHENS)) {
    institutionDetails.openAthensId = formData.openAthensId;
  } else {
    institutionDetails.openAthensId = null;
  }

  // Build and return the complete payload
  return {
    contact: {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      contactNumber: formData.contactNumber,
    },
    institution: {
      isExisting: isExisting,
    },
    organisation: {
      name: formData.organisation,
      abn: formData.abn,
    },
    subscriptions: formData.selectedProducts,
    institutionDetails,
    comment: formData.comment || null,
  };
};

/** * Validates ABN (Australian Business Number) format
 * @param {string} abn - ABN value
 * @returns {Object} { isValid: boolean, errorMessage: string }
 */
export const validateABN = abn => {
  // ABN is optional, so empty is valid
  if (!abn || abn.trim() === '') {
    return { isValid: true, errorMessage: '' };
  }

  // Remove spaces and hyphens
  const sanitized = abn.replace(/[\s-]/g, '');

  // ABN must be exactly 11 digits
  const abnPattern = /^\d{11}$/;
  if (!abnPattern.test(sanitized)) {
    return { isValid: false, errorMessage: 'ABN must be 11 digits' };
  }

  return { isValid: true, errorMessage: '' };
};

/** * Validates product selection
 * @param {Array} selectedProducts - Array of selected product IDs
 * @returns {Object} { isValid: boolean, errorMessage: string }
 */
export const validateProducts = selectedProducts => {
  if (selectedProducts.length === 0) {
    return { isValid: false, errorMessage: 'Please select at least one product' };
  }
  return { isValid: true, errorMessage: '' };
};

/**
 * Validates the configuration question (isAccountAdmin or isSameUserBase)
 * @param {boolean|null} configurationValue - The configuration value
 * @returns {Object} { isValid: boolean, errorMessage: string }
 */
export const validateConfigurationQuestion = configurationValue => {
  if (configurationValue === null) {
    return { isValid: false, errorMessage: 'Please answer all required questions' };
  }
  return { isValid: true, errorMessage: '' };
};

/**
 * Validates IP address field when IP authentication is selected
 * Supports IPv4 addresses and CIDR notation
 * @param {Array} authenticationType - Array of selected authentication types
 * @param {string} ipAddress - IP address value
 * @returns {Object} { isValid: boolean, errorMessage: string }
 */
export const validateIpAddress = (authenticationType, ipAddress) => {
  if (!authenticationType.includes(AUTH_TYPES.IP)) {
    return { isValid: true, errorMessage: '' };
  }

  if (!ipAddress || ipAddress.trim() === '') {
    return { isValid: false, errorMessage: 'Please enter IP address' };
  }

  // Validate IPv4 format with optional CIDR notation
  // Supports: 192.168.1.1, 192.168.1.0/24, or comma-separated list
  const ipPattern = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?(,\s*(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?)*$/;

  if (!ipPattern.test(ipAddress.trim())) {
    return {
      isValid: false,
      errorMessage: 'Please enter a valid IP address (e.g., 192.168.1.1 or 192.168.1.0/24)',
    };
  }

  // Validate each IP segment is between 0-255
  const ips = ipAddress.split(',').map(ip => ip.trim());
  for (const ip of ips) {
    const ipPart = ip.split('/')[0]; // Get IP without CIDR
    const segments = ipPart.split('.');
    for (const segment of segments) {
      const num = parseInt(segment, 10);
      if (num < 0 || num > 255) {
        return {
          isValid: false,
          errorMessage: 'IP address segments must be between 0 and 255',
        };
      }
    }
  }

  return { isValid: true, errorMessage: '' };
};

/**
 * Validates URL referrals when URL authentication is selected
 * @param {Array} authenticationType - Array of selected authentication types
 * @param {Array} selectedProducts - Array of selected product IDs
 * @param {Object} urlReferrals - URL referrals object
 * @returns {Object} { isValid: boolean, errorMessage: string }
 */
export const validateUrlReferrals = (authenticationType, selectedProducts, urlReferrals) => {
  if (!authenticationType.includes(AUTH_TYPES.URL)) {
    return { isValid: true, errorMessage: '' };
  }

  const missingUrls = selectedProducts.filter(
    productId => !urlReferrals[productId] || urlReferrals[productId].trim() === ''
  );
  if (missingUrls.length > 0) {
    return { isValid: false, errorMessage: 'Please enter URL referral for all products' };
  }

  // Validate URL format for provided URLs
  const urlPattern = /^(https?:\/\/)?([-\w.]+)\.([a-z.]{2,6})([/ \w.-]*)*\/?$/i;

  for (const productId of selectedProducts) {
    const url = urlReferrals[productId];
    if (url && url.trim() && !urlPattern.test(url.trim())) {
      return {
        isValid: false,
        errorMessage: 'Please enter valid URLs (e.g., https://example.com or example.com)',
      };
    }
  }

  return { isValid: true, errorMessage: '' };
};

/**
 * Validates OpenAthens ID when OpenAthens authentication is selected
 * @param {Array} authenticationType - Array of selected authentication types
 * @param {string} openAthensId - OpenAthens organization ID
 * @returns {Object} { isValid: boolean, errorMessage: string }
 */
export const validateOpenAthensId = (authenticationType, openAthensId) => {
  if (authenticationType.includes(AUTH_TYPES.OPENATHENS) && !openAthensId) {
    return { isValid: false, errorMessage: 'Please enter OpenAthens organization ID' };
  }
  return { isValid: true, errorMessage: '' };
};

/**
 * Validates all authentication-related fields
 * @param {Object} formData - Form state data
 * @param {Array} selectedProducts - Array of selected product IDs
 * @returns {Object} { isValid: boolean, errorMessage: string }
 */
export const validateAuthenticationFields = (formData, selectedProducts) => {
  const { authenticationType, ipAddress, urlReferrals, openAthensId } = formData;

  // Check authentication type selection
  let result;
  // Check IP address
  result = validateIpAddress(authenticationType, ipAddress);
  if (!result.isValid) return result;

  // Check URL referrals
  result = validateUrlReferrals(authenticationType, selectedProducts, urlReferrals);
  if (!result.isValid) return result;

  // Check OpenAthens ID
  result = validateOpenAthensId(authenticationType, openAthensId);
  if (!result.isValid) return result;

  return { isValid: true, errorMessage: '' };
};

/**
 * Validates the entire institution form
 * @param {Object} formData - Form state data
 * @param {boolean} isExisting - Whether this is an existing institution
 * @param {boolean} shouldValidateAuth - Whether to validate authentication fields
 * @returns {Object} { isValid: boolean, errorMessage: string }
 */
export const validateInstitutionForm = (formData, isExisting, shouldValidateAuth) => {
  const { selectedProducts, isAccountAdmin, isSameUserBase, abn } = formData;

  // Validate product selection
  let result = validateProducts(selectedProducts);
  if (!result.isValid) return result;

  // Validate ABN format
  result = validateABN(abn);
  if (!result.isValid) return result;

  // Validate configuration question
  const configurationValue = isExisting ? isSameUserBase : isAccountAdmin;
  result = validateConfigurationQuestion(configurationValue);
  if (!result.isValid) return result;

  // Validate authentication fields if required
  if (shouldValidateAuth) {
    result = validateAuthenticationFields(formData, selectedProducts);
    if (!result.isValid) return result;
  }

  return { isValid: true, errorMessage: '' };
};
